import React from 'react';
import { Badge, phone } from '@parsina/framework';
import { Ban, ShieldHalf } from 'lucide-react';

import { useTranslation, LTR_TEXT, localeDescriptor } from '../../i18n';
import { errorMessage } from '../../lib/api';
import { currencyLabel, EMPTY } from '../../lib/format';
import ActorLabel from '../../components/ActorLabel';
import {
  FALLBACK_COUNTRY, NOT_READ, fieldOfColumn, fieldSpec, isNotRead,
} from '../../constants/countryProfile';
import {
  capabilityValues, ceilingOf, describeColumn, describeMerged,
} from '../../lib/countryProfileView';

/**
 * The display half of «کشورها», shared by the list, the record, the editor and
 * the clinic card — so a column reads the same wherever it appears.
 */

/** One Intl.DisplayNames per locale and type; a code ICU does not know is echoed as itself. */
function useDisplayNames(type) {
  const { locale } = useTranslation();
  return React.useMemo(() => {
    try {
      return new Intl.DisplayNames([localeDescriptor(locale).intl], { type, fallback: 'code' });
    } catch {
      return null;
    }
  }, [locale, type]);
}

const nameOr = (names, code) => {
  try {
    return names?.of(code) || code;
  } catch {
    return code;
  }
};

/**
 * `IQ` → «عراق» / "Iraq"; `*` → «سایر کشورها». The kit's country catalogue first
 * (`phone.COUNTRIES` — the names the sign-in screen and every phone picker show,
 * in the panel's language), ICU's region names for a code it does not carry.
 * Names are data, not copy.
 */
export function useCountryName() {
  const { t, locale } = useTranslation();
  const names = useDisplayNames('region');
  return React.useCallback((code) => {
    if (code === FALLBACK_COUNTRY) return t('countries.fallbackName');
    if (!code) return EMPTY;
    const row = phone.findByIso2?.(code);
    const kitName = row ? phone.countryName?.(row, locale) : '';
    return kitName || nameOr(names, code);
  }, [names, t, locale]);
}

/** Whether ICU knows `code` as a region (its name is not the code echoed back). */
export function useKnownRegion() {
  const names = useDisplayNames('region');
  return React.useCallback((code) => {
    try {
      const name = names?.of(code);
      return !!name && name !== code;
    } catch {
      return false;
    }
  }, [names]);
}

/** The code as an LTR island beside its name. */
export function CountryCode({ code }) {
  return <code {...LTR_TEXT} className="font-mono text-xs text-muted-foreground">{code}</code>;
}

const weekdayDate = (iso) => new Date(Date.UTC(2024, 0, Number(iso), 12));   // 2024-01-01 was a Monday

/** Types whose value is an identifier — read left-to-right, never translated. */
const IDENTIFIER_TYPES = ['timezone', 'dialCode', 'key'];

/**
 * One value of one column as PLAIN TEXT — for an <option>, a sentence, a title:
 * a closed vocabulary through the catalogue (an unknown key echoed as itself),
 * a language through ICU, a weekday in the locale's own names, money's currency
 * through the panel's currency label.
 */
export function useValueString() {
  const {
    t, has, locale, formatWeekday, formatList,
  } = useTranslation();
  const languages = useDisplayNames('language');
  return React.useCallback((spec, value) => {
    switch (spec.type) {
      case 'language': return `${nameOr(languages, value)} (${value})`;
      case 'weekday': return formatWeekday(weekdayDate(value));
      case 'weekdays':
        return value.length ? formatList(value.map((d) => formatWeekday(weekdayDate(d)))) : t('countries.state.noWeekend');
      case 'currency': return currencyLabel(value, { t, locale });
      case 'dialCode': return `+${value}`;
      case 'mapView': return `${value.lat}, ${value.lon} · ${t('countries.values.zoom', { zoom: value.zoom })}`;
      case 'timezone':
      case 'key':
        return String(value);
      default: {
        const key = `countries.values.${spec.field}.${value}`;
        return has(key) ? t(key) : String(value);
      }
    }
  }, [t, has, locale, formatWeekday, formatList, languages]);
}

/** The same value as a node: identifiers (zone, dial code, coordinates, codes) as LTR islands. */
export function ValueText({ spec, value }) {
  const { t, has } = useTranslation();
  const valueString = useValueString();
  const island = (text) => <code {...LTR_TEXT} className="font-mono text-[12px]">{text}</code>;
  if (IDENTIFIER_TYPES.includes(spec.type)) return island(valueString(spec, value));
  if (spec.type === 'mapView') {
    return <span>{island(`${value.lat}, ${value.lon}`)} · {t('countries.values.zoom', { zoom: value.zoom })}</span>;
  }
  if (spec.type === 'enum' || spec.type === 'list') {
    return has(`countries.values.${spec.field}.${value}`) ? valueString(spec, value) : island(String(value));
  }
  return valueString(spec, value);
}

/** «فعلاً خوانده نمی‌شود» beside a column nothing reads this round, with the item that will (if the contract names one). */
export function NotReadBadge({ field }) {
  const { t } = useTranslation();
  if (!isNotRead(field)) return null;
  return (
    <Badge
      variant="outline"
      className="ms-1 whitespace-nowrap align-middle"
      title={NOT_READ[field]
        ? t('countries.state.notReadHint', { ref: NOT_READ[field] })
        : t('countries.state.notReadHintRound')}
      data-testid={`country-not-read-${field}`}
    >
      {t('countries.state.notRead')}
    </Badge>
  );
}

/**
 * The deployment ceiling beside a capability (CONTRACT.md §1 rule 4). Every
 * capability says that env, credentials and the currency rules still decide —
 * the row only narrows; one holding a value this deployment's market switches
 * off (an Iranian rail on Proxia) says THAT, plainly, because the row reads as
 * on and is not.
 */
export function CeilingNote({ spec, value, deployment }) {
  const { t, formatList } = useTranslation();
  const valueString = useValueString();
  const { offHere } = ceilingOf(deployment, spec);
  const off = capabilityValues(value).filter((v) => offHere.includes(v));
  if (off.length) {
    // A STATUS, not an error: the warning chip, never red (DESIGN_SYSTEM §1.1).
    return (
      <Badge
        variant="warning"
        className="inline-flex items-center gap-1"
        title={t('countries.ceiling.offHereHint')}
        data-testid={`country-ceiling-off-${spec.field}`}
      >
        <Ban size={12} aria-hidden />
        {t('countries.ceiling.offHere', { values: formatList(off.map((v) => valueString(spec, v))) })}
      </Badge>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      title={t('countries.ceiling.envHint')}
      data-testid={`country-ceiling-${spec.field}`}
    >
      <ShieldHalf size={12} aria-hidden />
      {t('countries.ceiling.env')}
    </span>
  );
}

/**
 * What NULL means for one default column when nothing resolves it — said per
 * column, because it is not always "today": no dial code means a number
 * without + is refused (`countries.nullMeans`).
 */
export const nullMeaning = (t, field) => t(`countries.nullMeans.${field}`);

/**
 * A column's value as the screen states it, NULL semantics included:
 * «پیرو *: …» or what the column's consumer does with no value, for a default;
 * «هیچ» for a capability. `merged` = the profile is already merged (a clinic's
 * effective one): there is nothing left to follow. A capability value this
 * deployment switches off is struck through.
 */
export function ColumnValue({
  spec, row, fallback, merged = false, deployment, testId,
}) {
  const { t } = useTranslation();
  const said = merged ? describeMerged(spec, row) : describeColumn(spec, row, fallback);
  const id = testId || `country-value-${spec.field}`;

  if (said.state === 'none') {
    return <span className="text-muted-foreground" data-state="none" data-testid={id}>{t('countries.state.none')}</span>;
  }
  if (said.state === 'serviceDefault') {
    return <span className="text-muted-foreground" data-state="service-default" data-testid={id}>{nullMeaning(t, spec.field)}</span>;
  }
  if (said.state === 'follows') {
    return (
      <span className="text-muted-foreground" data-state="follows" data-testid={id}>
        {said.resolved == null
          ? t('countries.state.followsNull', { meaning: nullMeaning(t, spec.field) })
          : <>{t('countries.state.follows')} <span className="text-foreground"><ValueText spec={spec} value={said.resolved} /></span></>}
      </span>
    );
  }
  const { offHere } = spec.kind === 'capability' ? ceilingOf(deployment, spec) : { offHere: [] };
  if (spec.type === 'list') {
    return (
      <span className="flex flex-wrap gap-1" data-state="value" data-testid={id}>
        {said.value.map((v) => (
          <Badge key={v} variant="outline" className={offHere.includes(v) ? 'line-through opacity-70' : undefined}>
            <ValueText spec={spec} value={v} />
          </Badge>
        ))}
      </span>
    );
  }
  return (
    <span className={offHere.includes(said.value) ? 'line-through opacity-70' : undefined} data-state="value" data-testid={id}>
      <ValueText spec={spec} value={said.value} />
    </span>
  );
}

/** A column's label from its service name (`changedFields`, removals), else the name itself. */
export function useColumnLabel() {
  const { t, has } = useTranslation();
  return React.useCallback((column) => {
    const field = fieldOfColumn(column);
    const key = `countries.fields.${field}`;
    return fieldSpec(field) && has(key) ? t(key) : column;
  }, [t, has]);
}

/** The editor of record: a person (a Keycloak sub), or the boot seed. */
export function EditorLabel({ by }) {
  const { t } = useTranslation();
  if (by === 'seed') return <span className="text-muted-foreground">{t('countries.seedActor')}</span>;
  return <ActorLabel actor={by} />;
}

const ERROR_KEYS = {
  EDITOR_DISABLED: 'countries.errors.editorDisabled',
  REGISTRY_NOT_DEPLOYED: 'countries.errors.registryNotDeployed',
  CONFLICT: 'countries.errors.conflict',
  NOTE_REQUIRED: 'countries.errors.noteRequired',
  CONFIRMATION_REQUIRED: 'countries.errors.confirmationRequired',
  INVALID_COUNTRY: 'countries.errors.invalidCountry',
  INVALID_COUNTRY_PROFILE: 'countries.errors.invalidProfile',
  INVALID_COUNTRY_SETTINGS: 'countries.errors.invalidSettings',
};

/**
 * A BFF error, worded from this panel's catalogue. For a refused value the
 * service's own message — technical English that names the column — follows
 * as an LTR island, because it is the useful part.
 */
export function countryErrorMessage(err, t) {
  const code = err?.code;
  if (!ERROR_KEYS[code]) return errorMessage(err, t);
  const detail = (code === 'INVALID_COUNTRY_PROFILE' || code === 'INVALID_COUNTRY_SETTINGS') && err?.message;
  if (!detail) return t(ERROR_KEYS[code]);
  return (
    <span>
      {t(ERROR_KEYS[code])}
      {' '}
      <code {...LTR_TEXT} className="font-mono text-[11px]">{err.message}</code>
    </span>
  );
}

const INSURER_ERROR_KEYS = {
  INSURER_EDITOR_DISABLED: 'countries.insurers.errors.editorDisabled',
  NOT_FOUND: 'countries.insurers.errors.notFound',
  INVALID_INSURER: 'countries.insurers.errors.invalid',
  INSURER_RULE: 'countries.insurers.errors.rule',
  INSURER_IMMUTABLE: 'countries.insurers.errors.immutable',
  SELF_PAY_NOT_EDITABLE: 'countries.insurers.errors.selfPay',
  INVALID_COUNTRY: 'countries.errors.invalidCountry',
  NOTE_REQUIRED: 'countries.errors.noteRequired',
};
/** Refusals whose technical message names the field — shown after the sentence. */
const INSURER_DETAIL = ['INVALID_INSURER', 'INSURER_RULE', 'INSURER_IMMUTABLE'];

/**
 * An insurer-editor error (PM-0246), worded from this panel's catalogue.
 * CONFLICT is two things: on a create, the id is taken; on an edit, the row
 * changed since the editor read it. A refused token is FORBIDDEN_DOWNSTREAM
 * and anything this map does not know falls through to the panel's own words.
 */
export function insurerErrorMessage(err, t, { creating = false } = {}) {
  const code = err?.code;
  if (code === 'CONFLICT') return t(creating ? 'countries.insurers.errors.idTaken' : 'countries.insurers.errors.changed');
  if (!INSURER_ERROR_KEYS[code]) return errorMessage(err, t);
  if (!INSURER_DETAIL.includes(code) || !err?.message) return t(INSURER_ERROR_KEYS[code]);
  return (
    <span>
      {t(INSURER_ERROR_KEYS[code])}
      {' '}
      <code {...LTR_TEXT} className="font-mono text-[11px]">{err.message}</code>
    </span>
  );
}

/**
 * An insurer's name in the reader's language, else English, else the Persian
 * original — the catalogue's names are data, never copy.
 */
export function insurerNameIn(insurer, locale) {
  if (!insurer) return '';
  const byLocale = Object.fromEntries((insurer.names || []).map((n) => [n.locale, n.name]));
  return byLocale[locale]
    || (locale === 'fa' ? insurer.nameFa : (byLocale.en || insurer.nameEn))
    || insurer.nameFa || insurer.nameEn || insurer.id || '';
}
